import { useCallback, useEffect, useState } from 'react'
import {
  ApiError,
  api,
  can,
  shortDate,
  type OnboardingProgress,
  type OnboardingStepReply,
  type OnboardingStepState,
} from '../lib/api'
import { ErrorBox, Loading } from './DataTable'
import { ProgressRing, Status, Toggletip, type Tone } from './kit'
import { ReasonTip } from './vendor/ReasonTip'
import '../styles/sweep.css'

// Where this vendor has got to, in the tenant's own named steps.
//
// # WHY EACH STEP SHOWS TWO THINGS
//
// A step is not one fact. It is what the product observed and what a person
// said, and they are drawn side by side because the case where they disagree is
// the one worth looking at: somebody has recorded a step the system cannot see,
// which is either work that genuinely happens elsewhere or a claim to ask
// about. Merging them into a tick would throw that away and give the console a
// second answer to "where is this vendor" that could differ from the gate's.
//
// # WHY THERE IS NO BUTTON ON EVERY STEP
//
// The API records a claim against any step, and says plainly when the claim
// counts for nothing. The console does not offer that. A button that records
// something inert teaches people the checklist is decoration, so the form
// appears only where an assertion can actually settle the step, which is where
// the tenant's own profile says so. On a step held to observation the answer is
// to do the thing, and the (i) beside its state says what the product looked
// for and did not find.
//
// Every sentence here is the API's, for the reason the activation checklist
// gives: nothing on this screen is composed from a second copy of the rules, so
// what is drawn and what the approve button refuses cannot drift apart.

// What is in force, said accurately. Three cases and not two: a version the
// seed installed at tenant setup is a real, versioned document, but crediting
// this organisation with configuring it would be telling them they decided
// something they have not yet read.
function processWords(p: OnboardingProgress): string {
  if (p.isDefault) return 'Shipped process, nobody here has configured their own'
  if (p.versionAuthor === 'seed') return 'Process version ' + p.versionNo + ', installed at setup and unchanged'
  return 'Process version ' + p.versionNo + ', as this organisation configured it'
}

function stepClass(s: OnboardingStepState): string {
  if (s.diverged) return 'act-item step-diverged'
  if (s.satisfied) return 'act-item act-met'
  if (s.mandatory) return 'act-item act-unmet'
  return 'act-item act-off'
}

// Done goes quiet; a step the vendor is held to and has not met is the one
// the eye should land on.
function stepWord(s: OnboardingStepState): { tone: Tone; text: string } {
  if (s.satisfied) return { tone: 'plain', text: 'Done' }
  if (s.mandatory) return { tone: 'warn', text: 'Outstanding' }
  return { tone: 'neutral', text: 'Not yet' }
}

function StepClaim({
  step,
  busy,
  onAssert,
  onWithdraw,
}: {
  step: OnboardingStepState
  busy: boolean
  onAssert: (note: string) => void
  onWithdraw: (reason: string) => void
}) {
  const [open, setOpen] = useState(false)
  const [text, setText] = useState('')
  const mayWrite = can('vendor.edit')
  const fieldId = 'step-claim-' + step.key

  if (step.asserted) {
    return (
      <div className="act-sign">
        <p className="act-said">
          <strong>{step.assertedBy}</strong> recorded this
          {step.assertedAt ? ' on ' + shortDate(step.assertedAt) : ''}: {step.assertedNote}
        </p>
        {mayWrite &&
          (open ? (
            <>
              <label htmlFor={fieldId}>Why is it being taken back?</label>
              <input
                id={fieldId}
                value={text}
                onChange={(e) => setText(e.target.value)}
                placeholder="Recorded against the wrong supplier"
                style={{ width: '100%', marginTop: 4 }}
              />
              <div className="chiprow" style={{ marginTop: 8 }}>
                <button
                  type="button"
                  className="primary"
                  disabled={busy || text.trim() === ''}
                  onClick={() => onWithdraw(text.trim())}
                  data-action="tprm.vendor,vendor-record.a22"
                >
                  {busy ? 'Recording' : 'Withdraw'}
                </button>
                <button type="button" onClick={() => setOpen(false)}>
                  Cancel
                </button>
              </div>
              {/* Said before it is done rather than after. The row does not go,
                  and somebody expecting it to disappear should know now. */}
              <p className="act-detail muted">Both stay on the record, the claim just stops counting</p>
            </>
          ) : (
            <div className="chiprow">
              <button type="button" onClick={() => setOpen(true)}>
                Take this back
              </button>
            </div>
          ))}
      </div>
    )
  }

  // Only where saying so can settle it. See the header.
  if (step.satisfiedBy === 'OBSERVED' || !mayWrite) return null

  return (
    <div className="act-sign">
      {open ? (
        <>
          <label htmlFor={fieldId}>What happened, and where can somebody check it?</label>
          <input
            id={fieldId}
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder="Raised in Ariba, requisition PR-4471"
            style={{ width: '100%', marginTop: 4 }}
          />
          <div className="chiprow" style={{ marginTop: 8 }}>
            <button
              type="button"
              className="primary"
              disabled={busy || text.trim() === ''}
              onClick={() => onAssert(text.trim())}
              data-action="tprm.vendor,vendor-record.a21"
            >
              {busy ? 'Recording' : 'Record this step'}
            </button>
            <button type="button" onClick={() => setOpen(false)}>
              Cancel
            </button>
          </div>
        </>
      ) : (
        <div className="chiprow">
          <button type="button" onClick={() => setOpen(true)}>
            Record this step
          </button>
        </div>
      )}
    </div>
  )
}

export function VendorOnboarding({ vendorId, onChanged }: { vendorId: string; onChanged?: () => void }) {
  const [progress, setProgress] = useState<OnboardingProgress | null>(null)
  const [error, setError] = useState<unknown>(null)
  const [busy, setBusy] = useState('')
  const [said, setSaid] = useState('')
  const [writeError, setWriteError] = useState<ApiError | null>(null)

  const load = useCallback(() => {
    api
      .vendorOnboarding(vendorId)
      .then((p) => {
        setProgress(p)
        setError(null)
      })
      .catch(setError)
  }, [vendorId])

  useEffect(load, [load])

  function write(key: string, run: () => Promise<OnboardingStepReply>) {
    setBusy(key)
    setWriteError(null)
    run()
      .then((r) => {
        // What the write produced, rather than a second read of a moving
        // target. The same reason the signature panel does it.
        setProgress(r.onboarding)
        setSaid(r.note ?? '')
        onChanged?.()
      })
      .catch((err) => setWriteError(err instanceof ApiError ? err : new ApiError(0, 'UNKNOWN', String(err))))
      .finally(() => setBusy(''))
  }

  if (error) return <ErrorBox error={error} what="This vendor's onboarding" />
  if (!progress) return <Loading what="the onboarding steps" />

  const done = progress.steps.filter((s) => s.satisfied).length
  const diverged = progress.steps.filter((s) => s.diverged).length

  return (
    <section className="panel panel-flush">
      <div className="panel-head">
        {/* Whether this organisation has configured its own process is
            behind the (i), so the heading carries it without a line of its
            own. */}
        <h3 style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
          Onboarding
          <Toggletip hover label="Which process this is">
            {processWords(progress)}
          </Toggletip>
        </h3>
      </div>
      <div className="act-body">
        {/* The outstanding steps are named once, by their own rows below,
            rather than again as a list in this line. */}
        <p className="summary-line sw-row">
          <ProgressRing done={done} total={progress.steps.length} label="Onboarding steps done" />
          {progress.mandatoryMet ? (
            <Status tone="ok">Required steps done</Status>
          ) : (
            <Status tone="warn">{progress.outstanding.length} required outstanding</Status>
          )}
          {diverged > 0 && (
            <>
              <Status tone="special">{diverged} not visible to the product</Status>
              <Toggletip label="What not visible means">
                Recorded by a person where the product cannot see it. Either the work happens elsewhere, or the claim is
                worth asking about.
              </Toggletip>
            </>
          )}
        </p>

        {said && <p className="ok-note">{said}</p>}
        {writeError && <ErrorBox error={writeError} what="That step" verb="record" />}

        <ol className="act-list">
          {progress.steps.map((s) => {
            const word = stepWord(s)
            return (
              <li key={s.key} className={stepClass(s)}>
                <div className="act-head">
                  <Status tone={word.tone}>{word.text}</Status>
                  {/* What the product looked for and found, behind a hover
                      beside the state it explains. */}
                  <ReasonTip hover label={'What the product saw for ' + s.name.toLowerCase()} text={s.observedDetail} />
                  <strong>{s.name}</strong>
                  <ReasonTip label={'Why ' + s.name.toLowerCase()} text={s.why} />
                  {!s.mandatory && <Status tone="plain">Optional</Status>}
                  {s.diverged && <Status tone="special">Not visible</Status>}
                </div>

                <StepClaim
                  step={s}
                  busy={busy === s.key}
                  onAssert={(note) => write(s.key, () => api.assertOnboardingStep(vendorId, s.key, note))}
                  onWithdraw={(reason) => write(s.key, () => api.withdrawOnboardingStep(vendorId, s.key, reason))}
                />

              </li>
            )
          })}
        </ol>
      </div>
    </section>
  )
}
